import { Link } from '@tanstack/react-router';
import { Button } from '@upcaret/ui';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  SmartDropdownMenu,
  SmartDropdownMenuTrigger,
} from '@upcaret/ui/dropdown-menu';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../contexts/auth-context';
import { BrandLink } from './brand-link';

export default function Header() {
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [desktopProductsOpen, setDesktopProductsOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const authButtonTo = isAuthenticated ? '/app/account' : '/app';
  const authButtonLabel = isAuthenticated ? 'Account' : 'Sign in';

  const topNavItemClass =
    'text-sm font-medium text-slate-700 transition-colors hover:text-primary';

  const products = [
    { to: '/products/extraction-api', label: 'Extraction API' },
    { to: '/products/website-agent', label: 'Website Agent' },
    { to: '/products/internal-agent', label: 'Internal Agent' },
    { to: '/products/lead-gen', label: 'Lead Gen' },
  ] as const;

  const services = [
    { to: '/products/automation-review', label: 'Automation Review' },
    { to: '/products/automation-support', label: 'Automation Support' },
    { to: '/products/openclaw-custom-development', label: 'OpenClaw' },
    { to: '/products/agentic-system-development', label: 'Agentic Systems' },
    { to: '/products/mcp', label: 'MCP' },
  ] as const;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/85 backdrop-blur supports-backdrop-filter:bg-white/70">
      <nav className="mx-auto w-full max-w-6xl min-w-0 px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex w-full min-w-0 items-center gap-3 sm:gap-4">
          <BrandLink className="shrink-0" />

          {/* Desktop: links take flexible space; Book + Sign in stay one unit so nothing wraps under the bar */}
          <div className="hidden min-w-0 flex-1 items-center md:flex">
            <div className="flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-x-3 md:gap-x-4 lg:gap-x-6">
            <SmartDropdownMenu
              open={desktopServicesOpen}
              onOpenChange={setDesktopServicesOpen}
            >
              <SmartDropdownMenuTrigger
                asChild
                open={desktopServicesOpen}
                onOpenChange={setDesktopServicesOpen}
              >
                <button
                  type="button"
                  className={[topNavItemClass, 'inline-flex items-center gap-1'].join(
                    ' ',
                  )}
                  style={{ touchAction: 'manipulation' }}
                  aria-label="Open services menu"
                >
                  Services
                  <ChevronDown className="ml-1 size-4" aria-hidden="true" />
                </button>
              </SmartDropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-56 rounded-xl border border-slate-200 bg-white p-1 text-popover-foreground shadow-md"
              >
                {services.map((s) => (
                  <DropdownMenuItem key={s.to} asChild>
                    <Link to={s.to} onClick={() => setDesktopServicesOpen(false)}>
                      {s.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </SmartDropdownMenu>
            <SmartDropdownMenu
              open={desktopProductsOpen}
              onOpenChange={setDesktopProductsOpen}
            >
              <SmartDropdownMenuTrigger
                asChild
                open={desktopProductsOpen}
                onOpenChange={setDesktopProductsOpen}
              >
                <button
                  type="button"
                  className={[topNavItemClass, 'inline-flex items-center gap-1'].join(
                    ' ',
                  )}
                  style={{ touchAction: 'manipulation' }}
                  aria-label="Open products menu"
                >
                  Products
                  <ChevronDown className="ml-1 size-4" aria-hidden="true" />
                </button>
              </SmartDropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-56 rounded-xl border border-slate-200 bg-white p-1 text-popover-foreground shadow-md"
              >
                {products.map((p) => (
                  <DropdownMenuItem key={p.to} asChild>
                    <Link to={p.to} onClick={() => setDesktopProductsOpen(false)}>
                      {p.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </SmartDropdownMenu>
            <Link
              to="/about"
              className={topNavItemClass}
            >
              About
            </Link>
            </div>
            <div className="flex shrink-0 items-center gap-2 pl-3 lg:gap-3 lg:pl-4">
            <Button
              asChild
              size="default"
              className="hidden min-w-0 max-w-full shrink bg-primary text-primary-foreground hover:bg-primary/90 md:inline-flex"
            >
              <Link to="/free-ai-audit-booking">Book free 15-min call</Link>
            </Button>

            <Button
              asChild
              variant="outline"
              className="shrink-0 border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-primary"
            >
              <Link to={authButtonTo}>{authButtonLabel}</Link>
            </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="ml-auto shrink-0 md:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-700 hover:bg-slate-100"
              onClick={() => {
                setMobileMenuOpen((open) => !open);
                setMobileProductsOpen(false);
                setMobileServicesOpen(false);
              }}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="mt-4 max-h-[min(32rem,calc(100dvh-5rem))] space-y-4 overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 md:hidden">
            <Link
              to="/"
              className="block text-sm font-medium text-slate-700 transition-colors hover:text-primary"
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileProductsOpen(false);
                setMobileServicesOpen(false);
              }}
            >
              Home
            </Link>
            <div className="space-y-2">
              <button
                type="button"
                className="flex w-full items-center justify-between text-sm font-medium text-slate-700 transition-colors hover:text-primary"
                onClick={() => setMobileServicesOpen((open) => !open)}
                aria-expanded={mobileServicesOpen}
              >
                <span>Services</span>
                <ChevronRight
                  className={[
                    'size-4 transition-transform',
                    mobileServicesOpen ? 'rotate-90' : '',
                  ].join(' ')}
                  aria-hidden="true"
                />
              </button>
              {mobileServicesOpen && (
                <div className="space-y-3 pl-4">
                  {services.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      className="block text-sm font-medium text-slate-700 transition-colors hover:text-primary"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileProductsOpen(false);
                        setMobileServicesOpen(false);
                      }}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <button
                type="button"
                className="flex w-full items-center justify-between text-sm font-medium text-slate-700 transition-colors hover:text-primary"
                onClick={() => setMobileProductsOpen((open) => !open)}
                aria-expanded={mobileProductsOpen}
              >
                <span>Products</span>
                <ChevronRight
                  className={[
                    'size-4 transition-transform',
                    mobileProductsOpen ? 'rotate-90' : '',
                  ].join(' ')}
                  aria-hidden="true"
                />
              </button>
              {mobileProductsOpen && (
                <div className="space-y-3 pl-4">
                  {products.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="block text-sm font-medium text-slate-700 transition-colors hover:text-primary"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileProductsOpen(false);
                        setMobileServicesOpen(false);
                      }}
                    >
                      {p.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/about"
              className="block text-sm font-medium text-slate-700 transition-colors hover:text-primary"
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileProductsOpen(false);
                setMobileServicesOpen(false);
              }}
            >
              About
            </Link>
            <Button
              asChild
              size="sm"
              className="w-full max-w-full"
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileProductsOpen(false);
                setMobileServicesOpen(false);
              }}
            >
              <Link to="/free-ai-audit-booking">Book free 15-min call</Link>
            </Button>
            <Button
              asChild
              className="w-full"
              variant="outline"
              size="sm"
              onClick={() => {
                setMobileMenuOpen(false);
                setMobileProductsOpen(false);
                setMobileServicesOpen(false);
              }}
            >
              <Link to={authButtonTo}>{authButtonLabel}</Link>
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
}
